/**
 * Pure helpers for the Incorrect Weight Charges "Fetch details" lookup
 * (pages/admin/IncorrectWeightChargesPanel.jsx). No React, no network — tested in
 * incorrectWeightLookup.test.js. The response shape is lookup_order() in
 * backend/services/incorrect_weight_charges_service.py.
 */

const DIMENSIONS = ["boxes", "length", "breadth", "height"];

/** True when the user has typed anything into the given dimension groups. */
export const hasEnteredDimensions = (groups) =>
  groups.some((g) => DIMENSIONS.some((f) => String(g[f] ?? "").trim() !== ""));

/** Whether a fetch would overwrite values: Total Boxes Sent or the Delhivery-record groups are filled. */
export const fetchWouldOverwrite = (shipment) =>
  String(shipment.totalBoxes ?? "").trim() !== "" || hasEnteredDimensions(shipment.incorrect);

/** A lookup response → the fields it fills (LRN, Order ID, Total Boxes, Delhivery-record groups). */
export function prefillFromLookup(result, makeGroup) {
  return {
    lrn: result.lrn,
    orderId: result.order_id,
    totalBoxes: String(result.total_boxes),
    incorrect: result.groups.map((g) => ({ ...makeGroup(), boxes: String(g.boxes), length: String(g.length),
      breadth: String(g.breadth), height: String(g.height) })),
  };
}

/** What to tell the user about a lookup result: { tone: "ok" | "warn", text }. */
export function lookupMessage(result) {
  switch (result.status) {
    case "found": {
      const skipped = result.skipped_boxes
        ? ` ${result.skipped_boxes} box(es) with missing dimensions were left out — add them by hand.` : "";
      const empty = result.groups.length ? "" : " The order has no box dimensions — enter them by hand.";
      return { tone: result.groups.length && !result.skipped_boxes ? "ok" : "warn",
        text: `Fetched from order ${result.order_id}.${skipped}${empty}` };
    }
    case "ambiguous": {
      const list = result.orders.map((o) => `${o.order_id} (${o.status || "no status"})`).join(", ");
      return { tone: "warn",
        text: `This LRN is on ${result.more ? "more than " : ""}${result.orders.length} orders: ${list}. Enter the Order ID and fetch again.` };
    }
    case "no_lrn":
      return { tone: "warn", text: `Order ${result.order_id} has no LRN yet (not manifested), so it cannot be used here.` };
    case "mismatch":
      return { tone: "warn", text: `That LRN does not belong to order ${result.order_id} (its LRN is ${result.lrn}). Check both.` };
    default:
      return { tone: "warn", text: "No matching order found — enter the details by hand." };
  }
}
